import { getChunks } from "@codemirror/merge";
import { EditorView, ViewPlugin } from "@codemirror/view";
import ReactDOM from "react-dom/client";
import createDomElement from "utils/createDomElement";

import { aiAssistantStateField } from "../../aiAssistantWidget/aiAssistantWidget";
import MergeActions from "./MergeActions/MergeActions";
import { navigateToChunk } from "./navigation";

export const mergePanel = (onAcceptAll: () => void, onRejectAll: () => void) =>
  ViewPlugin.fromClass(
    class {
      dom: HTMLDivElement | null = null;

      root: ReactDOM.Root | null = null;

      private scroller: HTMLElement | null = null;

      private aiWidgetElement: HTMLElement | null = null;

      private intersectionObserver: IntersectionObserver | null = null;

      // Whether the AI widget has left the viewport at least once
      private aiWidgetOutsideViewportOnce: boolean = false;

      constructor(private view: EditorView) {
        this.scroller = this.view.scrollDOM;
        this.ensure();
        // no initial sync, wait for first update
      }

      ensure() {
        if (this.dom && this.root) return;
        const dom = createDomElement("div", { className: "mergeControls" });
        this.dom = dom;
        this.view.dom.appendChild(dom);
        this.root = ReactDOM.createRoot(dom);
      }

      unmount() {
        // Defer root unmount to avoid React warning and re-entrancy with CM updates
        const root = this.root;
        if (root) {
          this.root = null;
          try {
            if (typeof queueMicrotask === "function") {
              queueMicrotask(() => root.unmount());
            } else {
              setTimeout(() => root.unmount(), 0);
            }
          } catch {
            setTimeout(() => root.unmount(), 0);
          }
        }
        if (this.dom) {
          this.dom.remove();
          this.dom = null;
        }
      }

      private unobserveAiWidget() {
        if (this.intersectionObserver) {
          if (this.aiWidgetElement)
            this.intersectionObserver.unobserve(this.aiWidgetElement);
          this.intersectionObserver.disconnect();
        }
      }

      private observeAiWidgetWithIntersectionObserver() {
        if (!this.scroller || !this.aiWidgetElement) return;

        this.intersectionObserver = new IntersectionObserver(
          entries => {
            const entry = entries[0];
            const visible = !!entry?.isIntersecting;
            if (!visible && !this.aiWidgetOutsideViewportOnce) {
              this.aiWidgetOutsideViewportOnce = true;
              // We don't need further observations once it left the viewport at least once
              this.unobserveAiWidget();
              this.render();
            }
          },
          { root: this.scroller, threshold: 0 }
        );
        this.intersectionObserver.observe(this.aiWidgetElement);
      }

      private syncAiWidgetObserver() {
        if (this.aiWidgetOutsideViewportOnce) return; // no need to observe anymore
        const aiWidgetState = this.view.state.field(
          aiAssistantStateField,
          false
        );
        const aiWidgetElement = aiWidgetState?.widgetInstance?.container;
        if (aiWidgetElement === this.aiWidgetElement) return;
        if (!aiWidgetElement) {
          // AI widget disappeared: stop observing previous element if any
          this.unobserveAiWidget();
          this.aiWidgetElement = null;
          return;
        }
        // Element changed: rebind observer
        this.unobserveAiWidget();
        this.aiWidgetElement = aiWidgetElement;
        this.observeAiWidgetWithIntersectionObserver();
      }

      private shouldShow(): boolean {
        const chunks = getChunks(this.view.state);
        const hasChunks = !!chunks && chunks.chunks.length > 0;
        return hasChunks && this.aiWidgetOutsideViewportOnce;
      }

      render() {
        const chunks = getChunks(this.view.state);
        if (!chunks || chunks.chunks.length === 0 || !this.shouldShow()) {
          this.unmount();
          return;
        }
        this.ensure();
        if (!this.root) return;
        const selection = this.view.state.selection.main;
        const chunkIndex = chunks.chunks.findIndex(
          c => c.fromB >= selection.to || c.toB > selection.from
        );
        const current = chunkIndex > -1 ? chunkIndex + 1 : chunks.chunks.length;
        const unresolved = chunks.chunks.length;
        this.root.render(
          <MergeActions
            onAcceptAll={() => onAcceptAll()}
            onRejectAll={() => onRejectAll()}
            onNext={() => navigateToChunk(this.view, 1)}
            onPrevious={() => navigateToChunk(this.view, -1)}
            currentChunk={current}
            unresolvedChunks={unresolved}
          />
        );
      }

      update() {
        // Rebind observer if AI widget element changed
        this.syncAiWidgetObserver();
        this.render();
      }

      destroy() {
        this.unobserveAiWidget();
        this.unmount();
      }
    }
  );
